import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import * as XLSX from 'xlsx';
import { parsePDFTo2DArray } from '../pdfParserService';

// Helper: Get collection name (same as backend)
const getCollectionName = (month, year) => {
    const m = String(month).padStart(2, '0');
    const name = `attendance_${year}_${m}`;
    console.log('[AttendanceService] Using collection:', name);
    return name;
};

/**
 * Parse date from string or excel number (Supports DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY)
 */
const parseFlexibleDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;

    const str = String(val).trim();
    // Handle separators: / or - or .
    const sep = str.includes('/') ? '/' : str.includes('-') ? '-' : str.includes('.') ? '.' : null;

    if (sep) {
        let parts = str.split(sep).map(Number);
        // Standard Excel/Regional: DD [sep] MM [sep] YYYY
        if (parts.length === 3) {
            let [d, m, y] = parts;
            // Sometimes it might be YYYY [sep] MM [sep] DD (ISO)
            if (d > 1000) {
                // It's likely YYYY-MM-DD
                return new Date(d, m - 1, y);
            }
            // Check if year is 2 or 4 digits
            const fullY = y < 100 ? 2000 + y : y;
            const finalDate = new Date(fullY, m - 1, d);
            if (!isNaN(finalDate.getTime())) return finalDate;
        }
    }

    // Fallback to standard JS parser
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

/**
 * Format Excel/JS time values to HH:mm
 */
const parseFlexibleTime = (val) => {
    if (val === null || val === undefined || val === '') return null;

    // 1. If it's a JS Date object
    if (val instanceof Date) {
        return val.getHours().toString().padStart(2, '0') + ':' +
            val.getMinutes().toString().padStart(2, '0');
    }

    // 2. If it's an Excel Serial Time (Number)
    // Excel stores times as fractions of a day (e.g., 0.5 is 12:00 PM)
    if (typeof val === 'number') {
        const totalMinutes = Math.round(val * 24 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        // Handle overflow (e.g. 24:00)
        const finalHours = hours % 24;
        return String(finalHours).padStart(2, '0') + ':' +
            String(minutes).padStart(2, '0');
    }

    // 3. If it's already a string
    const s = String(val).trim();
    if (!s) return null;

    // Standardize "9:30 AM" or "18:30" etc.
    const match = s.match(/(\d{1,2})[:.](\d{2})/);
    if (match) {
        let h = parseInt(match[1]);
        const m = match[2];
        if (s.toLowerCase().includes('pm') && h < 12) h += 12;
        if (s.toLowerCase().includes('am') && h === 12) h = 0;
        return String(h).padStart(2, '0') + ':' + m;
    }

    return s;
};

/**
 * Helper to commit batches in chunks
 */
/**
 * Fast helper to commit Firestore batches in parallel (chunks of up to 450 items)
 */
const commitBatchesInParallel = async (db, batchOps, chunkSize = 450) => {
    if (!batchOps || batchOps.length === 0) return;

    const batches = [];
    let currentBatch = writeBatch(db);
    let count = 0;

    for (const op of batchOps) {
        if (op.type === 'delete') {
            currentBatch.delete(op.ref);
        } else {
            currentBatch.set(op.ref, op.data);
        }
        count++;

        if (count >= chunkSize) {
            batches.push(currentBatch);
            currentBatch = writeBatch(db);
            count = 0;
        }
    }

    if (count > 0) {
        batches.push(currentBatch);
    }

    // Execute all batch commits concurrently in parallel
    await Promise.all(batches.map(b => b.commit()));
};

let cachedEmployeeMaps = null;
let cachedEmployeeMapsTime = 0;

export const getEmployeeMaps = async () => {
    const now = Date.now();
    // Cache valid for 5 minutes to avoid redundant network roundtrips
    if (cachedEmployeeMaps && (now - cachedEmployeeMapsTime < 5 * 60 * 1000)) {
        return cachedEmployeeMaps;
    }

    const idToNameMap = {};
    const idToDeptMap = {};
    try {
        const employeesRef = collection(db, 'employees');
        const empSnap = await getDocs(query(employeesRef));
        empSnap.forEach(doc => {
            const data = doc.data();
            const eid = String(data.employeeId || data.job?.employeeCode || doc.id).trim();
            const ename = data.personal?.fullName || data.fullName || `${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`.trim();
            if (eid) {
                idToNameMap[eid.toLowerCase()] = ename;
                if (data.department || data.job?.department) {
                    idToDeptMap[eid.toLowerCase()] = data.department || data.job.department;
                }
            }
        });
        cachedEmployeeMaps = { idToNameMap, idToDeptMap };
        cachedEmployeeMapsTime = now;
        console.log(`[AttendanceService] Loaded ${Object.keys(idToNameMap).length} employees for name lookup (cached)`);
    } catch (err) {
        console.warn('[AttendanceService] Could not fetch employee list for lookups:', err);
        return { idToNameMap: {}, idToDeptMap: {} };
    }
    return cachedEmployeeMaps;
};

/**
 * Upload bulk attendance (optimized for high speed and parallel operations)
 */
export const uploadBulkAttendance = async (file, overwriteDuplicates = false, targetMonth = null, targetYear = null, onProgress = null) => {
    try {
        console.log('[AttendanceService] Processing fast bulk upload');
        if (onProgress) onProgress(15, 'Reading file & employee profiles...');

        const isPDF = file.name.toLowerCase().endsWith('.pdf');

        // Parallelize file parsing and employee map resolution
        const [rawSheetData, empMaps] = await Promise.all([
            isPDF ? parsePDFTo2DArray(file) : (async () => {
                const data = await file.arrayBuffer();
                const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                return XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
            })(),
            getEmployeeMaps()
        ]);

        if (!rawSheetData || rawSheetData.length === 0) {
            throw new Error(`No data found in ${isPDF ? 'PDF' : 'Excel'} file`);
        }

        const { idToNameMap, idToDeptMap } = empMaps;
        if (onProgress) onProgress(35, `Scanning ${rawSheetData.length} rows...`);

        let currentEmployeeId = null;
        let currentEmployeeName = null;
        let currentDepartment = null;

        let successCount = 0;
        let failedCount = 0;
        const errors = [];
        const batchOps = [];

        // Common labels to find Employee ID and Name
        const idLabels = ['emp code', 'employee code', 'emp id', 'employee id', 'id', 'code', 'enroll no', 'card no', 'ac no', 'कोड', 'आयडी'];
        const nameLabels = ['employee name', 'emp name', 'name', 'full name', 'नाव'];
        const deptLabels = ['department', 'dept', 'division', 'unit', 'विभाग'];
        const dateLabels = ['date', 'att date', 'attn date', 'attendance date', 'att. date', 'log date', 'work date', 'तारीख', 'दिनांक'];

        // Row mapping to find columns for date/times
        let dateCol = -1, inCol = -1, outCol = -1, statusCol = -1, deptCol = -1;
        let shiftCol = -1, workDurationCol = -1, otCol = -1, lateCol = -1, earlyCol = -1;

        for (let i = 0; i < rawSheetData.length; i++) {
            const row = rawSheetData[i];
            if (!row || !Array.isArray(row) || row.length === 0) continue;

            const rowText = row.join(' | ').toLowerCase();

            // STEP 1: Look for Employee Context in this row
            // Some reports have: "Emp Code: 101 | Name: John"
            for (let j = 0; j < row.length; j++) {
                const cellVal = String(row[j] || '').trim();
                if (!cellVal) continue;
                const cellLower = cellVal.toLowerCase();

                const findValueAfterLabel = (labels) => {
                    for (const l of labels) {
                        if (cellLower.includes(l)) {
                            // Option A: Value is in the SAME cell after a colon "Emp Code: 101"
                            if (cellVal.includes(':')) {
                                const parts = cellVal.split(':');
                                if (parts[1] && parts[1].trim()) return parts[1].trim();
                            }

                            // Option B: Value is in subsequent cells (handles merged cells/spacing)
                            // Scan next 5 cells for a non-empty value
                            for (let k = 1; k <= 5; k++) {
                                if (row[j + k] && String(row[j + k]).trim()) {
                                    return String(row[j + k]).trim();
                                }
                            }
                        }
                    }
                    return null;
                };

                const extId = findValueAfterLabel(idLabels);
                if (extId && !dateLabels.some(dl => extId.toLowerCase().includes(dl))) {
                    currentEmployeeId = extId;
                }

                const extName = findValueAfterLabel(nameLabels);
                if (extName && !dateLabels.some(dl => extName.toLowerCase().includes(dl))) {
                    currentEmployeeName = extName;
                }

                const extDept = findValueAfterLabel(deptLabels);
                if (extDept) currentDepartment = extDept;
            }

            // STEP 2: Look for Header row to identify columns
            const hasDateLabel = dateLabels.some(l => rowText.includes(l));
            const hasTimeLabel = ['in', 'out', 'punch', 'arrival', 'depart', 'status'].some(l => rowText.includes(l));

            if (hasDateLabel && hasTimeLabel) {
                console.log('[AttendanceService] Header Row Candidate:', row);
                for (let j = 0; j < row.length; j++) {
                    const c = String(row[j] || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                    if (!c) continue;

                    // Date
                    if (['date', 'attdate', 'attndate', 'logdate', 'tarikh', 'dinank'].some(l => c.includes(l))) dateCol = j;

                    // In Time (Prefer specific labels, EXCLUDE Shift Times starting with 's')
                    const isInMatch = ['intime', 'checkin', 'punchin', 'arrival', 'start', 'clockin', 'in-time', 'punch-in'].some(l => c.includes(l)) || c === 'in';
                    if (isInMatch && !c.startsWith('s') && !c.includes('shift')) {
                        inCol = j;
                    }

                    // Out Time (EXCLUDE Shift Times starting with 's')
                    const isOutMatch = ['outtime', 'checkout', 'punchout', 'depart', 'end', 'clockout', 'out-time', 'punch-out'].some(l => c.includes(l)) || c === 'out';
                    if (isOutMatch && !c.startsWith('s') && !c.includes('shift')) {
                        outCol = j;
                    }

                    // Others
                    if (['status', 'attnstatus', 'remark', 'remark0'].some(l => c.includes(l))) statusCol = j;
                    if (['dept', 'department', 'unit'].some(l => c.includes(l))) deptCol = j;

                    // Specific logic for Shift Columns (S. InTime / S. OutTime)
                    if (['shift', 'sft'].some(l => c.includes(l)) || c.startsWith('s')) {
                        shiftCol = j;
                    }
                    if (['workduration', 'workdur', 'workinghours', 'duration', 'workhr', 'totalhours', 'totallog'].some(l => c.includes(l))) workDurationCol = j;
                    if (c === 'ot' || ['overtime', 'othours'].some(l => c.includes(l))) otCol = j;

                    if (['lateby', 'latearrival', 'latehr'].some(l => c.includes(l))) lateCol = j;
                    else if (c === 'late') lateCol = j;

                    if (['earlyby', 'earlygoing', 'earlyexit', 'earlydeparture'].some(l => c.includes(l))) earlyCol = j;
                    else if (c === 'early') earlyCol = j;
                }
                console.log('[AttendanceService] Mapping Discovered:', { dateCol, inCol, outCol, statusCol, shiftCol, workDurationCol, otCol, lateCol, earlyCol });
                continue;
            }

            // STEP 3: Process as Attendance Row
            if (currentEmployeeId && dateCol >= 0 && row[dateCol]) {
                const dateVal = String(row[dateCol]).trim();
                let parsedDate = parseFlexibleDate(dateVal);

                if (parsedDate) {
                    if (targetMonth !== null && targetYear !== null) {
                        const day = parsedDate.getDate();
                        parsedDate = new Date(targetYear, targetMonth - 1, day);
                    }
                    try {
                        const employeeId = String(currentEmployeeId).trim();

                        // Get name from Excel row or from the map (lookup from database)
                        const rawName = (currentEmployeeName || 'Unknown').trim();
                        const employeeName = (rawName === 'Unknown' || !rawName) ? (idToNameMap[employeeId.toLowerCase()] || 'Unknown') : rawName;

                        const inTime = inCol >= 0 ? parseFlexibleTime(row[inCol]) : null;
                        const outTime = outCol >= 0 ? parseFlexibleTime(row[outCol]) : null;
                        const rawStatus = statusCol >= 0 ? String(row[statusCol] || '').trim() : '';
                        // Default to WeeklyOff on Tuesdays, otherwise Present
                        const status = rawStatus || (parsedDate.getDay() === 2 ? 'WeeklyOff' : 'Present');

                        // Get department from Excel or from the map
                        const deptExcel = (deptCol >= 0 && row[deptCol]) ? String(row[deptCol]) : currentDepartment;
                        const dept = (deptExcel && deptExcel !== 'N/A') ? deptExcel : (idToDeptMap[employeeId.toLowerCase()] || null);

                        const month = parsedDate.getMonth() + 1;
                        const year = parsedDate.getFullYear();
                        const key = `${year}_${String(month).padStart(2, '0')}`;

                        // FIX: Use local date components instead of toISOString() to avoid timezone shifts
                        const d = String(parsedDate.getDate()).padStart(2, '0');
                        const m = String(parsedDate.getMonth() + 1).padStart(2, '0');
                        const y = parsedDate.getFullYear();
                        const dateString = `${y}-${m}-${d}`;

                        const docId = `${employeeId}_${dateString}`;
                        const ref = doc(db, `attendance_${key}`, docId);

                        let workingHours = null;
                        let overtimeHours = null;
                        const s = status.toUpperCase();
                        const isWorking = s === 'PRESENT' || s === 'P' || s === 'PRES' || s === 'LATE' || s.includes('HALF');

                        if (isWorking && inTime && outTime && String(inTime).trim() && String(outTime).trim()) {
                            const hours = calculateWorkingHours(String(inTime), String(outTime));
                            workingHours = hours.workingHours;
                            overtimeHours = hours.overtimeHours;
                        }

                        batchOps.push({
                            ref,
                            data: {
                                employeeId,
                                employeeName: String(employeeName).trim(),
                                department: dept ? String(dept).trim() : null,
                                date: Timestamp.fromDate(parsedDate),
                                dateString,
                                month,
                                year,
                                inTime,
                                outTime,
                                status: status || 'Present',
                                shift: shiftCol >= 0 ? String(row[shiftCol] || '') : null,
                                workDuration: workDurationCol >= 0 ? parseFlexibleTime(row[workDurationCol]) : workingHours,
                                overtime: otCol >= 0 ? parseFlexibleTime(row[otCol]) : overtimeHours,
                                lateBy: lateCol >= 0 ? parseFlexibleTime(row[lateCol]) : null,
                                earlyGoingBy: earlyCol >= 0 ? parseFlexibleTime(row[earlyCol]) : null,
                                isLocked: false,
                                uploadedAt: Timestamp.now(),
                                uploadedBy: 'Smart Parser'
                            }
                        });
                        successCount++;
                    } catch (err) {
                        failedCount++;
                        errors.push(`Row ${i + 1}: ${err.message}`);
                    }
                }
            }
        }

        if (batchOps.length === 0) {
            const firstErr = errors.length > 0 ? errors[0] : 'No valid attendance records found in file';
            throw new Error(`Upload Failed: Found 0 valid attendance records. ${firstErr}`);
        }

        // Identify all affected monthly collections (e.g. attendance_2026_10)
        const affectedCollections = [...new Set(batchOps.map(op => {
            const m = String(op.data.month).padStart(2, '0');
            const y = op.data.year;
            return `attendance_${y}_${m}`;
        }))];

        if (onProgress) onProgress(55, 'Checking existing month records...');

        // Query all affected collections concurrently in parallel
        const existingSnapshots = await Promise.all(
            affectedCollections.map(colName => getDocs(collection(db, colName)))
        );

        let totalExistingRecords = 0;
        const deleteOps = [];

        existingSnapshots.forEach(existingSnapshot => {
            const docsToDelete = existingSnapshot.docs.filter(docSnap => {
                const data = docSnap.data();
                return data.uploadedBy !== 'WRMS Extra Work Approval';
            });
            for (const docSnap of docsToDelete) {
                deleteOps.push({ type: 'delete', ref: docSnap.ref });
            }
            totalExistingRecords += docsToDelete.length;
        });

        // If existing records found and overwrite was not approved, report duplicate error for prompt
        if (totalExistingRecords > 0 && !overwriteDuplicates) {
            console.log(`[AttendanceService] Found ${totalExistingRecords} existing attendance records. Overwrite not requested.`);
            const err = new Error('DUPLICATE_RECORDS');
            err.code = 'DUPLICATE_RECORDS';
            err.data = {
                month: batchOps[0]?.data?.month || (targetMonth ? parseInt(targetMonth, 10) : new Date().getMonth() + 1),
                year: batchOps[0]?.data?.year || (targetYear ? parseInt(targetYear, 10) : new Date().getFullYear()),
                existingCount: totalExistingRecords,
                newCount: batchOps.length,
                empName: batchOps.length > 0 ? (batchOps[0].data.employeeName + (batchOps.length > 1 ? ' & others' : '')) : 'N/A',
                empCode: batchOps.length > 0 ? batchOps[0].data.employeeId : 'N/A'
            };
            throw err;
        }

        // If overwrite is requested and previous records exist, delete them concurrently in parallel!
        let deletedCount = 0;
        if (totalExistingRecords > 0 && overwriteDuplicates) {
            if (onProgress) onProgress(70, `Fast-deleting ${totalExistingRecords} previous records...`);
            console.log(`[AttendanceService] Overwrite enabled: Parallel deleting ${totalExistingRecords} previous records...`);
            await commitBatchesInParallel(db, deleteOps);
            deletedCount = totalExistingRecords;
            console.log(`[AttendanceService] Total ${deletedCount} previous attendance records successfully deleted in parallel.`);
        }

        // Fast-commit all new records in parallel batches!
        if (onProgress) onProgress(85, `Fast-saving ${batchOps.length} new records...`);
        await commitBatchesInParallel(db, batchOps);

        if (onProgress) onProgress(100, 'Upload complete!');

        console.log(`[AttendanceService] Successfully processed ${successCount} records. Total Failed: ${failedCount}, Previous Deleted: ${deletedCount}`);

        return {
            success: true,
            successCount,
            recordCount: successCount,
            failedCount,
            deletedCount,
            overwritten: totalExistingRecords > 0,
            empName: batchOps.length > 0 ? (batchOps[0].data.employeeName + (batchOps.length > 1 ? ' & others' : '')) : 'N/A',
            empCode: batchOps.length > 0 ? batchOps[0].data.employeeId : 'N/A',
            department: batchOps.length > 0 ? (batchOps[0].data.department || 'N/A') : 'N/A',
            month: batchOps.length > 0 ? batchOps[0].data.month : (targetMonth ? parseInt(targetMonth, 10) : (new Date().getMonth() + 1)),
            year: batchOps.length > 0 ? batchOps[0].data.year : (targetYear ? parseInt(targetYear, 10) : (new Date().getFullYear())),
            foundHeaders: rawSheetData.length > 0 ? rawSheetData[0] : [],
            foundHeadersPreview: rawSheetData.slice(0, 10).map(r => r.join(' | ')),
            headerScore: 5,
            errors: errors.slice(0, 10)
        };
    } catch (error) {
        console.error('Error in bulk upload:', error);
        throw error;
    }
};

/**
 * Upload attendance from Excel or PDF file
 * @param {File} file - Excel or PDF file
 * @param {number} month - Target month (1-12)
 * @param {number} year - Target year
 * @param {boolean} overwrite - Whether to delete and overwrite existing sheet (default: true)
 */
export const uploadAttendance = async (file, month, year, overwrite = true, onProgress = null) => {
    // Redirect to bulk upload for better performance, date handling, and duplicate cleanup
    return uploadBulkAttendance(file, overwrite, month, year, onProgress);
};

/**
 * Calculate working hours (matches backend logic)
 */
const calculateWorkingHours = (inTime, outTime) => {
    try {
        // Parse time strings (HH:MM format)
        const [inHour, inMin] = inTime.split(':').map(Number);
        const [outHour, outMin] = outTime.split(':').map(Number);

        const inMinutes = inHour * 60 + inMin;
        const outMinutes = outHour * 60 + outMin;

        const totalMinutes = outMinutes - inMinutes;
        const hours = totalMinutes / 60;

        // Overtime logic: Any time after 6:30 PM (18:30)
        const otThreshold = 18 * 60 + 30; // 1110 minutes
        const overtimeMinutes = outMinutes > otThreshold ? outMinutes - otThreshold : 0;
        const overtimeHours = overtimeMinutes / 60;

        return {
            workingHours: parseFloat(hours.toFixed(2)),
            overtimeHours: parseFloat(overtimeHours.toFixed(2))
        };
    } catch (error) {
        return { workingHours: null, overtimeHours: null };
    }
};

/**
 * Get attendance records with filters (matches backend)
 */
export const getAttendance = async (filters = {}) => {
    try {
        const { month, year, employeeId, status } = filters;

        if (!month || !year) {
            return {
                success: true,
                data: []
            };
        }

        const collectionName = getCollectionName(month, year);
        const attendanceRef = collection(db, collectionName);

        // Build query
        let q = query(attendanceRef);

        if (employeeId) {
            const rawId = String(employeeId).trim();
            const searchIds = [rawId];
            if (/^\d+$/.test(rawId)) {
                const n = parseInt(rawId, 10);
                searchIds.push(n, String(n), String(n).padStart(2, '0'), String(n).padStart(3, '0'));
            }
            const uniqueIds = [...new Set(searchIds)].slice(0, 10);
            q = query(attendanceRef, where('employeeId', 'in', uniqueIds));
        }
        if (status) {
            q = query(q, where('status', '==', status));
        }

        const snapshot = await getDocs(q);
        const records = [];

        snapshot.forEach((doc) => {
            const data = doc.data();
            records.push({
                id: doc.id,
                ...data,
                date: data.date?.toDate?.() || data.date,
                uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt,
                lockedAt: data.lockedAt?.toDate?.() || data.lockedAt,
            });
        });

        // Sort by date (newest first)
        records.sort((a, b) => new Date(b.date) - new Date(a.date));

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching attendance:', error);
        throw new Error(error.message || 'Failed to fetch attendance');
    }
};

/**
 * Helper to normalize employee IDs for robust comparison (e.g., "05", "5", 5 -> "5")
 */
export const normalizeEmpId = (id) => {
    if (id === null || id === undefined) return '';
    const s = String(id).trim();
    if (/^\d+$/.test(s)) {
        return String(parseInt(s, 10));
    }
    return s.toLowerCase();
};

/**
 * Robust check to see if two employee names refer to the same person.
 * Matches exact, substring, or First + Last name (handles middle names like "Yasin" in "Zarin Yasin Jamadar" vs "Zarin Jamadar")
 */
export const areNamesMatching = (nameA, nameB) => {
    if (!nameA || !nameB) return false;
    const a = String(nameA).toLowerCase().trim();
    const b = String(nameB).toLowerCase().trim();
    if (a === b || a.includes(b) || b.includes(a)) return true;
    const partsA = a.replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(Boolean);
    const partsB = b.replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(Boolean);
    if (partsA.length >= 2 && partsB.length >= 2) {
        if (partsA[0] === partsB[0] && partsA[partsA.length - 1] === partsB[partsB.length - 1]) {
            return true;
        }
    }
    return false;
};

/**
 * Get monthly attendance (matches backend)
 * @param {number} month - Month number (1-12)
 * @param {number} year - Year
 * @param {string} empCode - Employee code/ID to filter by
 * @param {string} empName - Employee name to filter by (optional, used as fallback)
 */
export const getMonthlyAttendance = async (month, year, empCode = null, empName = null) => {
    try {
        const collectionName = getCollectionName(month, year);
        console.log('[AttendanceService] getMonthlyAttendance:', { month, year, empCode, empName, collectionName });
        const attendanceRef = collection(db, collectionName);

        let records = [];
        let searchIds = [];

        if (empCode) {
            const rawCode = String(empCode).trim();
            searchIds.push(rawCode);

            // Add numeric variations (e.g., 5, "5", "05", "005")
            if (/^\d+$/.test(rawCode)) {
                const num = parseInt(rawCode, 10);
                searchIds.push(num);
                searchIds.push(String(num));
                searchIds.push(String(num).padStart(2, '0'));
                searchIds.push(String(num).padStart(3, '0'));
            }

            // Look up UID and all aliases if empCode is provided
            try {
                const empsRef = collection(db, 'employees');
                const empQ1 = query(empsRef, where('employeeCode', '==', empCode));
                const empQ2 = query(empsRef, where('employeeId', '==', empCode));
                const empQ3 = query(empsRef, where('authUserId', '==', empCode));
                const empQ4 = query(empsRef, where('uid', '==', empCode));
                const docRef = doc(db, 'employees', empCode);

                const [snap1, snap2, snap3, snap4, docSnap] = await Promise.all([
                    getDocs(empQ1), getDocs(empQ2), getDocs(empQ3), getDocs(empQ4), getDoc(docRef)
                ]);

                const processDoc = (docSnapItem) => {
                    if (!docSnapItem.exists()) return;
                    searchIds.push(docSnapItem.id);
                    const d = docSnapItem.data();
                    const aliasFields = [d.authUserId, d.uid, d.id, d.employeeId, d.job?.employeeCode, d.employeeCode];
                    aliasFields.forEach(alias => {
                        if (!alias) return;
                        searchIds.push(alias);
                        const s = String(alias).trim();
                        searchIds.push(s);
                        if (/^\d+$/.test(s)) {
                            const n = parseInt(s, 10);
                            searchIds.push(n);
                            searchIds.push(String(n));
                            searchIds.push(String(n).padStart(2, '0'));
                            searchIds.push(String(n).padStart(3, '0'));
                        }
                    });

                    if (!empName) {
                        const fName = d.personalInfo?.firstName || d.firstName || '';
                        const lName = d.personalInfo?.lastName || d.lastName || '';
                        if (fName || lName) {
                            empName = `${fName} ${lName}`.trim();
                        } else if (d.name || d.displayName) {
                            empName = d.name || d.displayName;
                        }
                    }
                };

                snap1.forEach(processDoc);
                snap2.forEach(processDoc);
                snap3.forEach(processDoc);
                snap4.forEach(processDoc);
                processDoc(docSnap);

                // Also try users collection just in case
                const usersRef = collection(db, 'users');
                const userQ = query(usersRef, where('employeeCode', '==', empCode));
                const userSnap = await getDocs(userQ);
                userSnap.forEach(userDoc => {
                    searchIds.push(userDoc.id);
                    if (userDoc.data().teamId) searchIds.push(userDoc.data().teamId);
                });
            } catch (err) {
                console.error("Error fetching UID for fallback:", err);
            }

            // Remove duplicates
            const uniqueSearchIds = [...new Set(searchIds)];

            // Query in chunks of 10 to respect Firestore 'in' limit
            for (let i = 0; i < uniqueSearchIds.length; i += 10) {
                const chunk = uniqueSearchIds.slice(i, i + 10);
                try {
                    const q = query(attendanceRef, where('employeeId', 'in', chunk));
                    const snapshot = await getDocs(q);
                    snapshot.forEach((docSnapItem) => {
                        const data = docSnapItem.data();
                        if (!records.some(r => r.id === docSnapItem.id)) {
                            records.push({
                                id: docSnapItem.id,
                                ...data,
                                date: data.date?.toDate?.() || data.date,
                                uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt,
                            });
                        }
                    });
                } catch (qErr) {
                    console.warn('[AttendanceService] Query chunk error:', qErr);
                }
            }

            console.log('[AttendanceService] Found', records.length, 'records by exact empCode, variations or UID matches');
        }

        // Comprehensive Fallback: If records count is low (< 20) or 0 and we have empCode or empName,
        // scan all month attendance records to ensure no biometric or legacy upload records are missed
        if (empCode || empName) {
            if (records.length < 20) {
                console.log('[AttendanceService] Fewer than 20 records (' + records.length + '), checking client-side matching across month records');
                try {
                    const allSnapshot = await getDocs(query(attendanceRef));
                    const searchEmpCodeNorm = normalizeEmpId(empCode);

                    allSnapshot.forEach((docSnapItem) => {
                        if (records.some(r => r.id === docSnapItem.id)) return; // Already included

                        const data = docSnapItem.data();
                        const recordEmpId = data.employeeId || data.empCode || data.id || '';
                        const recordEmpIdNorm = normalizeEmpId(recordEmpId);
                        const recordEmpName = data.employeeName || '';

                        let isMatch = false;

                        // 1. Normalized numeric or string ID match ("05" === "5")
                        if (searchEmpCodeNorm && recordEmpIdNorm && searchEmpCodeNorm === recordEmpIdNorm) {
                            isMatch = true;
                        }

                        // 2. Direct searchIds match
                        if (!isMatch && searchIds.length > 0 && searchIds.some(sid => String(sid).toLowerCase() === String(recordEmpId).toLowerCase())) {
                            isMatch = true;
                        }

                        // 3. Robust Name match (e.g. "Zarin Jamadar" <-> "Zarin Yasin Jamadar")
                        if (!isMatch && empName && areNamesMatching(recordEmpName, empName)) {
                            isMatch = true;
                        }

                        if (isMatch) {
                            records.push({
                                id: docSnapItem.id,
                                ...data,
                                date: data.date?.toDate?.() || data.date,
                                uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt,
                            });
                        }
                    });

                    console.log('[AttendanceService] Total records after client-side matching:', records.length);
                } catch (csErr) {
                    console.error('[AttendanceService] Client-side matching error:', csErr);
                }
            }
        }

        // If still no records and no filter, return all
        if (records.length === 0 && !empCode && !empName) {
            const allSnapshot = await getDocs(query(attendanceRef));
            allSnapshot.forEach((docSnapItem) => {
                const data = docSnapItem.data();
                records.push({
                    id: docSnapItem.id,
                    ...data,
                    date: data.date?.toDate?.() || data.date,
                    uploadedAt: data.uploadedAt?.toDate?.() || data.uploadedAt,
                });
            });
        }

        // Sort by date
        records.sort((a, b) => new Date(a.date) - new Date(b.date));

        return {
            success: true,
            data: records
        };
    } catch (error) {
        console.error('Error fetching monthly attendance:', error);
        throw new Error(error.message || 'Failed to fetch monthly attendance');
    }
};

/**
 * Get attendance summary for employee (matches backend)
 */
export const getAttendanceSummary = async (employeeId, month, year) => {
    try {
        console.log('[AttendanceService] getAttendanceSummary called with:', { employeeId, month, year });

        const collectionName = getCollectionName(month, year);
        const attendanceRef = collection(db, collectionName);

        // First try exact match
        let q = query(attendanceRef, where('employeeId', '==', employeeId));
        let snapshot = await getDocs(q);

        // If no results, try with string conversion (in case of numeric IDs)
        if (snapshot.empty && employeeId) {
            console.log('[AttendanceService] No exact match, trying string conversion');
            q = query(attendanceRef, where('employeeId', '==', String(employeeId)));
            snapshot = await getDocs(q);
        }

        // If still no results, fetch all and filter client-side (handles partial matches)
        if (snapshot.empty && employeeId) {
            console.log('[AttendanceService] No match, fetching all and filtering client-side');
            const allSnapshot = await getDocs(query(attendanceRef));
            const matchingRecords = [];

            allSnapshot.forEach((doc) => {
                const data = doc.data();
                const recordEmpId = String(data.employeeId || '').toLowerCase();
                const searchEmpId = String(employeeId).toLowerCase();

                // Match if IDs are equal or one contains the other
                if (recordEmpId === searchEmpId ||
                    recordEmpId.includes(searchEmpId) ||
                    searchEmpId.includes(recordEmpId)) {
                    matchingRecords.push(data);
                }
            });

            console.log('[AttendanceService] Found', matchingRecords.length, 'matching records');

            if (matchingRecords.length === 0) {
                return {
                    success: true,
                    data: {
                        employeeId,
                        month: parseInt(month),
                        year: parseInt(year),
                        totalDays: 0,
                        presentDays: 0,
                        absentDays: 0,
                        leaveDays: 0,
                        halfDays: 0,
                        weeklyOffs: 0,
                        holidays: 0,
                        paidDays: 0,
                        lwpDays: 0,
                        totalWorkingHours: 0,
                        overtimeHours: 0
                    }
                };
            }

            // Use matching records for calculation
            const records = matchingRecords;
            return calculateSummaryFromRecords(employeeId, month, year, records);
        }

        if (snapshot.empty) {
            return {
                success: true,
                data: {
                    employeeId,
                    month: parseInt(month),
                    year: parseInt(year),
                    totalDays: 0,
                    presentDays: 0,
                    absentDays: 0,
                    leaveDays: 0,
                    halfDays: 0,
                    weeklyOffs: 0,
                    holidays: 0,
                    paidDays: 0,
                    lwpDays: 0,
                    totalWorkingHours: 0,
                    overtimeHours: 0
                }
            };
        }

        const records = [];
        snapshot.forEach((doc) => {
            records.push(doc.data());
        });

        return calculateSummaryFromRecords(employeeId, month, year, records);
    } catch (error) {
        console.error('Error fetching attendance summary:', error);
        throw new Error(error.message || 'Failed to fetch attendance summary');
    }
};

/**
 * Helper function to calculate summary from records
 */
const calculateSummaryFromRecords = (employeeId, month, year, records) => {
    // Calculate statistics (matches backend logic)
    const totalDays = records.length;
    const presentDays = records.filter(r => r.status === 'Present').length;
    const absentDays = records.filter(r => r.status === 'Absent').length;
    const leaveDays = records.filter(r => r.status === 'Leave').length;
    const halfDays = records.filter(r => r.status === 'Half-day').length;
    const weeklyOffs = records.filter(r => r.status === 'WeeklyOff').length;
    const holidays = records.filter(r => r.status === 'Holiday').length;

    const paidDays = presentDays + leaveDays + holidays + weeklyOffs + (halfDays * 0.5);
    const lwpDays = absentDays;

    const totalWorkingHours = records.reduce((sum, r) => sum + (parseFloat(r.workingHours) || 0), 0);
    const overtimeHours = records.reduce((sum, r) => sum + (parseFloat(r.overtimeHours) || 0), 0);

    return {
        success: true,
        data: {
            employeeId,
            employeeName: records[0]?.employeeName || 'Unknown',
            month: parseInt(month),
            year: parseInt(year),
            totalDays,
            presentDays,
            absentDays,
            leaveDays,
            halfDays,
            weeklyOffs,
            holidays,
            paidDays: parseFloat(Number(paidDays).toFixed(2)),
            lwpDays,
            totalWorkingHours: parseFloat(Number(totalWorkingHours).toFixed(2)),
            overtimeHours: parseFloat(Number(overtimeHours).toFixed(2))
        }
    };
};

/**
 * Update attendance record
 */
export const updateAttendance = async (recordId, data) => {
    try {
        // Extract month/year from data to get correct collection
        const month = data.month;
        const year = data.year;

        if (!month || !year) {
            throw new Error('Month and year are required');
        }

        const collectionName = getCollectionName(month, year);
        const attendanceRef = doc(db, collectionName, recordId);

        // Check if record exists
        const attendanceDoc = await getDoc(attendanceRef);
        if (!attendanceDoc.exists()) {
            throw new Error('Attendance record not found');
        }

        // Prepare update data
        const updateData = {
            ...data,
            updatedAt: Timestamp.now()
        };

        // Convert dates if provided
        if (data.date) {
            updateData.date = Timestamp.fromDate(new Date(data.date));
        }

        await updateDoc(attendanceRef, updateData);

        return {
            success: true,
            message: 'Attendance updated successfully',
            data: {
                id: recordId,
                ...updateData
            }
        };
    } catch (error) {
        console.error('Error updating attendance:', error);
        throw new Error(error.message || 'Failed to update attendance');
    }
};

/**
 * Get list of uploaded months
 */
export const getUploadedMonths = async (empCode = null) => {
    try {
        // Search range (2024 to 2027) to cover past and upcoming uploads
        const startYear = 2024;
        const endYear = 2027;
        const months = [];

        for (let y = startYear; y <= endYear; y++) {
            for (let m = 1; m <= 12; m++) {
                const collectionName = getCollectionName(m, y);
                const attendanceRef = collection(db, collectionName);

                // Quick check for at least 1 record
                const q = query(attendanceRef, limit(1));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    months.push(`${y}-${String(m).padStart(2, '0')}`);
                }
            }
        }

        return {
            success: true,
            data: [...new Set(months)].sort().reverse() // Unique sorted list
        };
    } catch (error) {
        console.error('Error fetching uploaded months:', error);
        throw new Error(error.message || 'Failed to fetch uploaded months');
    }
};


/**
 * Download attendance template
 */
export const downloadAttendanceTemplate = async () => {
    try {
        // Create template data
        const templateData = [
            {
                'Employee ID': 'EMP001',
                'Employee Name': 'John Doe',
                'Date': '2024-01-01',
                'In Time': '09:00',
                'Out Time': '18:00',
                'Status': 'Present'
            },
            {
                'Employee ID': 'EMP002',
                'Employee Name': 'Jane Smith',
                'Date': '2024-01-01',
                'In Time': '09:30',
                'Out Time': '17:30',
                'Status': 'Present'
            }
        ];

        // Create workbook
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Attendance');

        // Download file
        XLSX.writeFile(wb, 'attendance_template.xlsx');

        return { success: true };
    } catch (error) {
        console.error('Error downloading template:', error);
        throw new Error('Failed to download template');
    }
};

/**
 * Mark Extra Work in Attendance
 * Called when an Extra Work request is finally approved in WRMS
 */
export const markExtraWorkInAttendance = async (employeeId, employeeName, department, workDate, startTime, endTime, totalHours) => {
    try {
        if (!workDate || !employeeId) {
            throw new Error('Missing workDate or employeeId');
        }

        const dateObj = new Date(workDate);
        const y = dateObj.getFullYear();
        const m = dateObj.getMonth() + 1;
        const d = dateObj.getDate();

        const paddedMonth = String(m).padStart(2, '0');
        const paddedDay = String(d).padStart(2, '0');
        const dateString = `${y}-${paddedMonth}-${paddedDay}`;
        
        const collectionName = `attendance_${y}_${paddedMonth}`;
        const docId = `${employeeId}_${dateString}`;
        const ref = doc(db, collectionName, docId);

        const snap = await getDoc(ref);
        
        if (snap.exists()) {
            // Update existing record to Extra Work
            await updateDoc(ref, {
                status: 'Extra Work',
                inTime: startTime || snap.data().inTime,
                outTime: endTime || snap.data().outTime,
                workDuration: totalHours || snap.data().workDuration,
                overtime: totalHours || snap.data().overtime,
                updatedAt: Timestamp.now()
            });
        } else {
            // Create new record
            await setDoc(ref, {
                employeeId,
                employeeName: employeeName || 'Unknown',
                department: department || null,
                date: Timestamp.fromDate(dateObj),
                dateString,
                month: m,
                year: y,
                inTime: startTime || '-',
                outTime: endTime || '-',
                status: 'Extra Work',
                workDuration: totalHours || '0',
                overtime: totalHours || '0',
                isLocked: false,
                uploadedAt: Timestamp.now(),
                uploadedBy: 'WRMS Extra Work Approval'
            });
        }
        console.log(`[AttendanceService] Successfully marked Extra Work for ${employeeId} on ${dateString}`);
        return { success: true };
    } catch (error) {
        console.error('[AttendanceService] Error marking extra work:', error);
        throw error;
    }
};

/**
 * Delete all attendance records for a specific month and year
 * @param {number} month - Month (1-12)
 * @param {number} year - Year
 * @param {boolean} preserveExtraWork - Whether to preserve WRMS approved extra work
 */
export const deleteAttendanceForMonth = async (month, year, preserveExtraWork = true) => {
    try {
        const collectionName = getCollectionName(month, year);
        const attendanceRef = collection(db, collectionName);
        const snapshot = await getDocs(attendanceRef);

        if (snapshot.empty) {
            return { success: true, deletedCount: 0 };
        }

        const docsToDelete = snapshot.docs.filter(docSnap => {
            if (!preserveExtraWork) return true;
            return docSnap.data().uploadedBy !== 'WRMS Extra Work Approval';
        });

        if (docsToDelete.length === 0) {
            return { success: true, deletedCount: 0 };
        }

        const deleteOps = docsToDelete.map(docSnap => ({ type: 'delete', ref: docSnap.ref }));
        await commitBatchesInParallel(db, deleteOps);

        console.log(`[AttendanceService] Deleted ${deleteOps.length} records in parallel from ${collectionName}`);
        return { success: true, deletedCount: deleteOps.length };
    } catch (error) {
        console.error('Error deleting attendance for month:', error);
        throw new Error(error.message || 'Failed to delete attendance records');
    }
};

const attendanceService = {
    uploadAttendance,
    uploadBulkAttendance,
    downloadAttendanceTemplate,
    getAttendance,
    getMonthlyAttendance,
    getAttendanceSummary,
    updateAttendance,
    getUploadedMonths,
    markExtraWorkInAttendance,
    deleteAttendanceForMonth
};

export default attendanceService;

